import type React from 'react';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui';
import { ChevronLeftIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useKeepModalVisibleAboveKeyboard } from '#src/app/shared/hooks/dom';

function noop(): void {
	/* see the `onClose` comment on the Modal below */
}

// Styling the back button has to go through `leadingSlotClassName`: ModalHeader clones the slot with
// `{ size: 'medium', className: leadingSlotClassName }`, so a `className` on the Button itself is
// dropped. That forced `size="medium"` also matches none of Button's real sizes (large | small |
// x-small), which is why the focus ring is spelled out here rather than inherited from the variant.
const BACK_BUTTON_CLASSNAME = cn(
	'rounded-full p-1 no-underline',
	'focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-solid',
	'focus-visible:outline-stroke-focus',
);

type ComposeSheetAction = {
	label: string;
	onClick: () => void;
	disabled?: boolean;
	loading?: boolean;
};

type Props = {
	isOpen: boolean;
	/** Single dismissal path: the close button, ESC and outside-click all arrive here. */
	onOpenChange: (isOpen: boolean) => void;
	/** Uppercase kicker above the heading — "Your reason", "Review message", … */
	eyebrow: string;
	heading: string;
	/**
	 * Omit on the first stage, which has nowhere to go back to. The label is not optional: the button
	 * is icon-only, so it has no accessible name without one.
	 */
	back?: { onClick: () => void; ariaLabel: string };
	/** Full-width footer CTA. Omit for a stage with no primary action (the drafting spinner). */
	primaryAction?: ComposeSheetAction;
	/**
	 * Underlined link above the primary CTA — "Continue with my own words". Takes `disabled` because
	 * it can carry the supporter forward with a payload of its own, so it needs the same guard the
	 * primary does; it never shows a spinner.
	 */
	tertiaryAction?: Omit<ComposeSheetAction, 'loading'>;
	children: React.ReactNode;
};

/**
 * Bottom-sheet chrome shared by the compose stages (component spec, "Shared: bottom-sheet chrome").
 *
 * Each stage renders its own `ComposeSheet` with its own eyebrow, heading and footer, rather than one
 * sheet swapping its insides. That mirrors `PetitionShareSlideUp`.
 *
 * Because of that, a sheet unmounts on every stage change while still `isOpen`, so this component
 * cannot own focus restoration — `ContactDecisionMakerFlow` does it once for the whole flow.
 */
export const ComposeSheet: React.FC<Props> = ({
	isOpen,
	onOpenChange,
	eyebrow,
	heading,
	back,
	primaryAction,
	tertiaryAction,
	children,
}) => {
	const { translate } = useI18n();
	const anchorRef = useKeepModalVisibleAboveKeyboard();

	return (
		<Modal
			isOpen={isOpen}
			// `onClose` only exists to make the DS render the X; it must stay a no-op so dismissal does
			// not double-fire. See PetitionShareSlideUp.
			onClose={noop}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			onOpenChange={onOpenChange}
			aria-label={heading}
			className="z-100"
		>
			{/* Must stay a DIRECT child of `Modal` — see PetitionShareSlideUp. */}
			<ModalHeader
				title={
					<div className="flex flex-col gap-1">
						{/* Spec asks for `--color-typography-weaker`, which Sunrise doesn't ship; `weak` is the
						    weakest text token that exists. */}
						<Text as="span" className="text-size-body-xs-bold text-typography-weak tracking-[0.06em] uppercase">
							{eyebrow}
						</Text>
						{/* Spec calls for 21px/800, which sits between two steps of the type scale; rounded down to
						    heading-xs (18px/700) to stay on-token, matching the sibling share sheet's 19px → xs. */}
						<Text as="span" className="text-size-heading-xs text-typography-strong">
							{heading}
						</Text>
					</div>
				}
				leadingSlotClassName={BACK_BUTTON_CLASSNAME}
				leadingSlot={
					back ? (
						// iconOnly buttons render no children — the accessible name is the aria-label.
						<Button
							iconOnly
							variant="tertiary"
							leadingIcon={<ChevronLeftIcon />}
							aria-label={back.ariaLabel}
							onClick={back.onClick}
						/>
					) : undefined
				}
			/>

			<ModalBody className="max-h-[calc(85dvh-80px)]" data-testid="contact-decision-maker-compose-sheet-body">
				<div ref={anchorRef} className="w-full">
					{children}
				</div>
			</ModalBody>

			{/* The text link renders ABOVE the primary on small screens, as the design draws every two-CTA
			    screen ("MVP - All Supporters.dc.html", `storyFooter`). It is done with `order-first` on the link
			    rather than by reordering the children: the DS asserts the primary Button is ModalFooter's
			    first child and owns the buttons' flex container (`flex-col`, `md:flex-row-reverse`), so a
			    `className` here never reaches it. At `md` and up the DS's own row-reverse layout applies:
			    primary on the right, link on the left. CHANGE-98939. */}
			{(primaryAction || tertiaryAction) && (
				<ModalFooter displayFooterTopSeparator data-testid="contact-decision-maker-compose-sheet-footer">
					{/* A static (non-function) ModalFooter body has DS v13 call `close()` after every Button's
					    own onClick — meant for simple confirm/cancel footers. Our actions drive the flow's state
					    machine, not the modal's open state, so the render-prop form opts back out of that and
					    leaves closing to `onOpenChange` (ESC, outside click, the header's X). */}
					{() => (
						<>
							{primaryAction && (
								<Button
									variant="primary"
									className="w-full"
									disabled={primaryAction.disabled}
									loading={primaryAction.loading}
									onClick={primaryAction.onClick}
								>
									{primaryAction.label}
								</Button>
							)}
							{tertiaryAction && (
								<Button
									variant="tertiary"
									size="small"
									className="order-first mx-auto underline md:order-none"
									onClick={tertiaryAction.onClick}
									disabled={tertiaryAction.disabled}
								>
									{tertiaryAction.label}
								</Button>
							)}
						</>
					)}
				</ModalFooter>
			)}
		</Modal>
	);
};
